/**
 * MyGram Go yadrosiga HTTP klient.
 *
 * Konvert (mavjud yadro, `httpx`):
 *   muvaffaqiyat → { ok: true,  data: T }
 *   xato         → { ok: false, error: { code, message } }
 *
 * Auth: admin JWT (`aud=admin`, 8 soat), `Authorization: Bearer <token>`.
 * `ParseAccess` user-tokenni admin route'ida RAD etadi va aksincha — shuning
 * uchun bu yerda faqat admin token ishlatiladi.
 */

import { getToken, clearSession } from './auth'

export const API_BASE = '/api/v1'

/** Yadro qaytaradigan xato — `code` bilan, foydalanuvchiga matn tarjima qilinadi. */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Sessiya tugagan/bekor qilingan — A1.05 ekraniga olib boradi. */
  get isAuth(): boolean {
    return this.status === 401 || this.code === 'SESSION_REVOKED'
  }

  /** Huquq yetmaydi — <PermissionDenied> ko'rsatiladi. */
  get isForbidden(): boolean {
    return this.status === 403
  }
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error?: { code?: string; message?: string } }

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Query parametrlar — undefined/null qiymatlar tashlab yuboriladi. */
  query?: Record<string, string | number | boolean | undefined | null>
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = API_BASE + path
  if (!query) return url
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `${url}?${s}` : url
}

/**
 * Sessiya tugaganda butun ilova bir joyda xabardor bo'lishi uchun.
 * `AuthProvider` shu hodisani tinglaydi va A1.05 ni ko'rsatadi.
 */
export const SESSION_EXPIRED_EVENT = 'grom:session-expired'

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers['Authorization'] = `Bearer ${token}`

  let res: Response
  try {
    res = await fetch(buildUrl(path, opts.query), {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    })
  } catch (e) {
    // Tarmoq uzilishi — `AbortError` ni qayta uloqtirmaymiz, chaqiruvchi hal qiladi.
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError('NETWORK', 'network_unreachable', 0)
  }

  const json = (await res.json().catch(() => ({}))) as Envelope<T>

  if (res.status === 401) {
    clearSession()
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT))
    throw new ApiError('UNAUTHORIZED', 'session_expired', 401)
  }

  if (!res.ok || !('ok' in json) || json.ok !== true) {
    const err = 'error' in json ? json.error : undefined
    throw new ApiError(err?.code ?? 'UNKNOWN', err?.message ?? `HTTP ${res.status}`, res.status)
  }

  return json.data
}

export const api = {
  get: <T>(path: string, o?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...o, method: 'GET' }),
  post: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...o, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...o, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, o?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...o, method: 'PATCH', body }),
  del: <T>(path: string, o?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...o, method: 'DELETE' }),
}
